import { Network, NetworkCondition } from '../types';

/**
 * Network/Club Configuration
 * 
 * This configuration defines all available networks and their conditional routing rules.
 * Each network can have different behaviors:
 * - Standard flow: Continue with normal signup process
 * - Redirect to sales: Redirect users to sales team (e.g., SBG)
 * - Requires approval: Need manual approval before account creation
 * - Custom onboarding: Special onboarding process
 */

export const NETWORKS: Network[] = [
  // Alphabetical order by displayName, with "Other" at the end
  {
    id: 'adnfc',
    name: 'ADNFC',
    displayName: 'ADNFC',
    description: 'ADNFC network members',
    requiresApproval: false,
    redirectToSales: false,
    hubspotSettings: {
      customPipeline: 'adnfc-network',
      additionalProperties: {
        lead_source: 'ADNFC',
        network_type: 'network'
      }
    }
  },
  {
    id: 'advance_mortgage_funding',
    name: 'AMF',
    displayName: 'Advance Mortgage Funding',
    description: 'Advance Mortgage Funding network members',
    requiresApproval: false,
    redirectToSales: false,
    hubspotSettings: {
      customPipeline: 'advance-mortgage',
      additionalProperties: {
        lead_source: 'Advance Mortgage Funding',
        network_type: 'network'
      }
    }
  },
  {
    id: 'altura',
    name: 'ALTURA',
    displayName: 'Altura',
    description: 'Altura network members',
    requiresApproval: false,
    redirectToSales: false,
    hubspotSettings: {
      customPipeline: 'altura',
      additionalProperties: {
        lead_source: 'Altura',
        network_type: 'network'
      }
    }
  },
  {
    id: 'beneficial',
    name: 'BENEFICIAL',
    displayName: 'Beneficial',
    description: 'Beneficial network members',
    requiresApproval: false,
    redirectToSales: false,
    hubspotSettings: {
      customPipeline: 'beneficial',
      additionalProperties: {
        lead_source: 'Beneficial',
        network_type: 'network'
      }
    }
  },
  {
    id: 'da',
    name: 'DA',
    displayName: 'DA',
    description: 'DA network members',
    requiresApproval: false,
    redirectToSales: false,
    hubspotSettings: {
      customPipeline: 'da-network',
      additionalProperties: {
        lead_source: 'DA',
        network_type: 'network'
      }
    }
  },
  {
    id: 'directly_authorised',
    name: 'DIRECT',
    displayName: 'Directly Authorised',
    description: 'Independent directly authorised advisers',
    requiresApproval: false,
    redirectToSales: false,
    hubspotSettings: {
      customPipeline: 'direct-signup',
      additionalProperties: {
        lead_source: 'Direct Website',
        signup_type: 'self_service'
      }
    }
  },
  {
    id: 'hlp',
    name: 'HLP',
    displayName: 'HLP',
    description: 'HLP network members',
    requiresApproval: false,
    redirectToSales: false,
    hubspotSettings: {
      customPipeline: 'hlp-network',
      additionalProperties: {
        lead_source: 'HLP',
        network_type: 'network'
      }
    }
  },
  {
    id: 'ingard',
    name: 'INGARD',
    displayName: 'Ingard',
    description: 'Ingard network members',
    requiresApproval: false,
    redirectToSales: false,
    hubspotSettings: {
      customPipeline: 'ingard',
      additionalProperties: {
        lead_source: 'Ingard',
        network_type: 'network'
      }
    }
  },
  {
    id: 'inpartnership',
    name: 'IP',
    displayName: 'InPartnership',
    description: 'InPartnership network members',
    requiresApproval: false,
    redirectToSales: false,
    hubspotSettings: {
      customPipeline: 'inpartnership',
      additionalProperties: {
        lead_source: 'InPartnership',
        network_type: 'network'
      }
    }
  },
  {
    id: 'intrinsic',
    name: 'INTRINSIC',
    displayName: 'Intrinsic',
    description: 'Intrinsic network members',
    requiresApproval: false,
    redirectToSales: false,
    hubspotSettings: {
      customPipeline: 'intrinsic',
      additionalProperties: {
        lead_source: 'Intrinsic',
        network_type: 'network'
      }
    }
  },
  {
    id: 'just_mortgages',
    name: 'JM',
    displayName: 'Just Mortgages',
    description: 'Just Mortgages network members',
    networkId: '18bdf480-e77e-4f00-9e1b-2315a38c39a4',
    requiresApproval: false,
    redirectToSales: false,
    hubspotSettings: {
      customPipeline: 'just-mortgages',
      additionalProperties: {
        lead_source: 'Just Mortgages',
        network_type: 'branded'
      }
    }
  },
  {
    id: 'lg',
    name: 'L&G',
    displayName: 'L&G',
    description: 'Legal & General network',
    requiresApproval: false,
    redirectToSales: false,
    hubspotSettings: {
      customPipeline: 'lg-network',
      additionalProperties: {
        lead_source: 'L&G',
        network_type: 'provider'
      }
    }
  },
  {
    id: 'money_group',
    name: 'MONEY',
    displayName: 'Money Group',
    description: 'Money Group network members',
    requiresApproval: false,
    redirectToSales: false,
    hubspotSettings: {
      customPipeline: 'money-group',
      additionalProperties: {
        lead_source: 'Money Group',
        network_type: 'network'
      }
    }
  },
  {
    id: 'mortgage_connect_ifa',
    name: 'MC',
    displayName: 'Mortgage Connect / IFA',
    description: 'Mortgage Connect and IFA network members',
    requiresApproval: false,
    redirectToSales: false,
    hubspotSettings: {
      customPipeline: 'mortgage-connect',
      additionalProperties: {
        lead_source: 'Mortgage Connect / IFA',
        network_type: 'network'
      }
    }
  },
  {
    id: 'new_leaf',
    name: 'NEWLEAF',
    displayName: 'New Leaf',
    description: 'New Leaf network members',
    requiresApproval: false,
    redirectToSales: false,
    hubspotSettings: {
      customPipeline: 'new-leaf',
      additionalProperties: {
        lead_source: 'New Leaf',
        network_type: 'network'
      }
    }
  },
  {
    id: 'next_intelligence',
    name: 'NEXTINTEL',
    displayName: 'Next Intelligence',
    description: 'Next Intelligence network members',
    requiresApproval: false,
    redirectToSales: false,
    hubspotSettings: {
      customPipeline: 'next-intelligence',
      additionalProperties: {
        lead_source: 'Next Intelligence',
        network_type: 'network'
      }
    }
  },
  {
    id: 'openwork',
    name: 'OPENWORK',
    displayName: 'Openwork',
    description: 'Openwork network members',
    requiresApproval: false,
    redirectToSales: false,
    hubspotSettings: {
      customPipeline: 'openwork',
      additionalProperties: {
        lead_source: 'Openwork',
        network_type: 'network'
      }
    }
  },
  {
    id: 'paradigm',
    name: 'PARADIGM',
    displayName: 'Paradigm',
    description: 'Paradigm network members',
    requiresApproval: false,
    redirectToSales: false,
    hubspotSettings: {
      customPipeline: 'paradigm-network',
      additionalProperties: {
        lead_source: 'Paradigm',
        network_type: 'network'
      }
    }
  },
  {
    id: 'pi_financial',
    name: 'PI',
    displayName: 'Pi Financial',
    description: 'Pi Financial network members',
    requiresApproval: false,
    redirectToSales: false,
    hubspotSettings: {
      customPipeline: 'pi-financial',
      additionalProperties: {
        lead_source: 'Pi Financial',
        network_type: 'network'
      }
    }
  },
  {
    id: 'pkd',
    name: 'PKD',
    displayName: 'PKD',
    description: 'PKD network members',
    requiresApproval: false,
    redirectToSales: false,
    hubspotSettings: {
      customPipeline: 'pkd-network',
      additionalProperties: {
        lead_source: 'PKD',
        network_type: 'network'
      }
    }
  },
  {
    id: 'pms',
    name: 'PMS',
    displayName: 'PMS',
    description: 'PMS network members',
    requiresApproval: false,
    redirectToSales: false,
    hubspotSettings: {
      customPipeline: 'pms-network',
      additionalProperties: {
        lead_source: 'PMS',
        network_type: 'network'
      }
    }
  },
  {
    id: 'primis',
    name: 'PRIMIS',
    displayName: 'PRIMIS',
    description: 'PRIMIS network members',
    requiresApproval: false,
    redirectToSales: false,
    hubspotSettings: {
      customPipeline: 'primis',
      additionalProperties: {
        lead_source: 'PRIMIS',
        network_type: 'network'
      }
    }
  },
  {
    id: 'quilter',
    name: 'QUILTER',
    displayName: 'Quilter',
    description: 'Quilter network members',
    requiresApproval: false,
    redirectToSales: false,
    hubspotSettings: {
      customPipeline: 'quilter-network',
      additionalProperties: {
        lead_source: 'Quilter',
        network_type: 'provider'
      }
    }
  },
  {
    id: 'right_mortgage',
    name: 'RM',
    displayName: 'Right Mortgage',
    description: 'Right Mortgage network members',
    requiresApproval: false,
    redirectToSales: false,
    hubspotSettings: {
      customPipeline: 'right-mortgage',
      additionalProperties: {
        lead_source: 'Right Mortgage',
        network_type: 'network'
      }
    }
  },
  {
    id: 'rosemount',
    name: 'ROSEMOUNT',
    displayName: 'Rosemount',
    description: 'Rosemount network members',
    requiresApproval: false,
    redirectToSales: false,
    hubspotSettings: {
      customPipeline: 'rosemount',
      additionalProperties: {
        lead_source: 'Rosemount',
        network_type: 'network'
      }
    }
  },
  {
    id: 'sesame',
    name: 'SESAME',
    displayName: 'Sesame Bankhall Group',
    description: 'Sesame Bankhall Group network members',
    networkId: '60d2a953-1798-45ff-842f-d27dc6e63e2d',
    requiresApproval: false,
    redirectToSales: false,
    hubspotSettings: {
      customPipeline: 'sesame-network',
      additionalProperties: {
        lead_source: 'Sesame Bankhall Group',
        network_type: 'network'
      }
    }
  },
  {
    id: 'simply_biz',
    name: 'SIMPLYBIZ',
    displayName: 'Simply Biz',
    description: 'Simply Biz network members',
    requiresApproval: false,
    redirectToSales: false,
    hubspotSettings: {
      customPipeline: 'simply-biz',
      additionalProperties: {
        lead_source: 'Simply Biz',
        network_type: 'network'
      }
    }
  },
  {
    id: 'stonebridge',
    name: 'STONEBRIDGE',
    displayName: 'Stonebridge',
    description: 'Stonebridge network members',
    requiresApproval: false,
    redirectToSales: false,
    hubspotSettings: {
      customPipeline: 'stonebridge',
      additionalProperties: {
        lead_source: 'Stonebridge',
        network_type: 'network'
      }
    }
  },
  {
    id: 'tenet',
    name: 'TENET',
    displayName: 'Tenet',
    description: 'Tenet network members',
    requiresApproval: false,
    redirectToSales: false,
    hubspotSettings: {
      customPipeline: 'tenet-network',
      additionalProperties: {
        lead_source: 'Tenet',
        network_type: 'network'
      }
    }
  },
  // "Other" option always at the end
  {
    id: 'other',
    name: 'OTHER',
    displayName: 'Other',
    description: 'Other network not listed above',
    requiresApproval: false,
    redirectToSales: false,
    hubspotSettings: {
      customPipeline: 'other',
      additionalProperties: {
        lead_source: 'Other',
        network_type: 'other'
      }
    }
  }
];

/**
 * Network Conditions Configuration
 * 
 * Defines what happens when each network is selected
 */
export const NETWORK_CONDITIONS: NetworkCondition[] = [
  // SBG - Redirect to sales
  {
    networkId: 'sbg',
    condition: 'redirect_to_sales',
    action: {
      type: 'redirect',
      target: 'https://smartr365.com/contact-sales?ref=sbg',
      message: 'Redirecting you to our sales team for enterprise setup...',
      data: {
        utm_source: 'signup_form',
        utm_campaign: 'sbg_partner',
        priority: 'high'
      }
    }
  },
  
  // Commercial Finance - Redirect to specialized sales
  {
    networkId: 'commercial_finance',
    condition: 'redirect_to_sales',
    action: {
      type: 'redirect',
      target: 'https://smartr365.com/commercial-finance-contact',
      message: 'Connecting you with our commercial finance specialists...',
      data: {
        utm_source: 'signup_form',
        utm_campaign: 'commercial_finance',
        specialization: 'commercial'
      }
    }
  },
  
  // Mortgage Club Premier - Requires approval
  {
    networkId: 'mortgage_club_1',
    condition: 'requires_approval',
    action: {
      type: 'show_message',
      message: 'Your application will be reviewed within 24 hours. You\'ll receive an email with next steps.',
      data: {
        approval_required: true,
        review_sla: '24_hours'
      }
    }
  },
  
  // Equity Release - Requires approval with compliance review
  {
    networkId: 'equity_release',
    condition: 'requires_approval',
    action: {
      type: 'show_message',
      message: 'Due to regulatory requirements, your application needs compliance approval (typically 48 hours).',
      data: {
        approval_required: true,
        compliance_review: true,
        review_sla: '48_hours'
      }
    }
  },
  
  // Protection Plus - Custom onboarding flow
  {
    networkId: 'protection_plus',
    condition: 'custom_onboarding',
    action: {
      type: 'continue_form',
      message: 'Your account will include specialized protection tools and workflows.',
      data: {
        custom_onboarding: true,
        specialization: 'protection',
        additional_setup: ['protection_calculators', 'specialist_templates']
      }
    }
  }
];

/**
 * Get network by ID
 */
export const getNetworkById = (id: string): Network | null => {
  return NETWORKS.find(network => network.id === id) || null;
};

/**
 * Get network condition by network ID
 */
export const getNetworkCondition = (networkId: string): NetworkCondition | null => {
  return NETWORK_CONDITIONS.find(condition => condition.networkId === networkId) || null;
};

/**
 * Check if network requires sales redirect
 */
export const shouldRedirectToSales = (networkId: string): boolean => {
  const network = getNetworkById(networkId);
  return network?.redirectToSales || false;
};

/**
 * Check if network requires approval
 */
export const requiresApproval = (networkId: string): boolean => {
  const network = getNetworkById(networkId);
  return network?.requiresApproval || false;
};

/**
 * Get sales redirect URL for network
 */
export const getSalesRedirectUrl = (networkId: string): string | null => {
  const network = getNetworkById(networkId);
  return network?.salesRedirectUrl || null;
};

/**
 * Get conditional message for network
 */
export const getConditionalMessage = (networkId: string): string | null => {
  const network = getNetworkById(networkId);
  return network?.conditionalMessage || null;
};

/**
 * Default network (fallback)
 */
export const DEFAULT_NETWORK_ID = 'directly_authorised';